import { isIsoDate, isIsoDateTime, isIsoLocalTime } from '@lc/libdates'
import { escapeHtml } from '@lc/libfmt'
import { idAssert, magicCast } from '@lc/libts'
import { safeProp } from '@lc/libutils'
import type { TranslationContext } from '../types/ctx'
import type { Translate, TranslationInfo } from '../types/functions'
import type {
	TranslationKey,
	TranslationKeyConcat,
	TranslationKeyDate,
	TranslationKeyDateTime,
	TranslationKeyEnum,
	TranslationKeyInterpolate,
	TranslationKeyList,
	TranslationKeyPlural,
	TranslationKeyRef,
	TranslationKeyTime,
} from '../types/keys'
import type { TranslationValueKind } from '../types/values'
import { fmtDate, fmtDateTime, fmtTime } from './fmt'
import { getLf, getPr } from './intl'

const translateImpls = {
	interpolate: (
		ctx: TranslationContext,
		key: TranslationKeyInterpolate,
		params: Record<string, unknown>,
	): string => {
		void ctx
		const param = params[safeProp(key.key)]
		if (typeof param !== 'string' && typeof param !== 'number')
			throw new TypeError(
				`Param ${key.key} is not string or number (${typeof param})`,
			)
		const value = '' + param
		if (!key.html) return escapeHtml(value)
		else return value
	},
	concat: (
		ctx: TranslationContext,
		key: TranslationKeyConcat,
		params: Record<string, unknown>,
	): string => {
		return key.parts.map(key => translateImpl(ctx, key, params)).join('')
	},
	date: (
		ctx: TranslationContext,
		key: TranslationKeyDate,
		params: Record<string, unknown>,
	): string => {
		const param = params[safeProp(key.key)]
		if (typeof param !== 'string' && !(param instanceof Date))
			throw new TypeError(
				`Param ${key.key} is not string or Date (${typeof param})`,
			)
		if (typeof param === 'string' && !isIsoDate(param))
			throw new TypeError(
				`Param ${key.key} (${param}) is not a valid date`,
			)
		return fmtDate(ctx, param, key.fmt)
	},
	time: (
		ctx: TranslationContext,
		key: TranslationKeyTime,
		params: Record<string, unknown>,
	): string => {
		const param = params[safeProp(key.key)]
		if (typeof param !== 'string' && !(param instanceof Date))
			throw new TypeError(
				`Param ${key.key} is not string or Date (${typeof param})`,
			)
		if (typeof param === 'string' && !isIsoLocalTime(param))
			throw new TypeError(
				`Param ${key.key} (${param}) is not a valid time`,
			)
		return fmtTime(ctx, param, key.fmt)
	},
	datetime: (
		ctx: TranslationContext,
		key: TranslationKeyDateTime,
		params: Record<string, unknown>,
	): string => {
		const param = params[safeProp(key.key)]
		if (typeof param !== 'string' && !(param instanceof Date))
			throw new TypeError(
				`Param ${key.key} is not string or Date (${typeof param})`,
			)
		if (typeof param === 'string' && !isIsoDateTime(param))
			throw new TypeError(
				`Param ${key.key} (${param}) is not a valid datetime`,
			)
		return fmtDateTime(ctx, param, key.fmt)
	},
	list: (
		ctx: TranslationContext,
		key: TranslationKeyList,
		params: Record<string, unknown>,
	): string => {
		const param = params[safeProp(key.key)]
		if (!Array.isArray(param) || param.some(x => typeof x !== 'string'))
			throw new TypeError(
				`Param ${key.key} is not string[] (${typeof param})`,
			)
		idAssert<string[]>(param)
		return getLf(ctx.id, key.type, key.fmt).format(param)
	},
	plural: (
		ctx: TranslationContext,
		key: TranslationKeyPlural,
		params: Record<string, unknown>,
	): string => {
		let param = params[safeProp(key.key)]
		if (typeof param !== 'number' && !Array.isArray(param))
			throw new TypeError(
				`Param ${key.key} is not number or any[] (${typeof param})`,
			)
		if (Array.isArray(param)) param = param.length
		idAssert<number>(param)
		const plurality = getPr(ctx.id, key.type).select(param)
		// eslint-disable-next-line security/detect-object-injection
		const branch = key.choices[plurality] ?? key.choices._
		return translateImpl(ctx, branch, params)
	},
	ref: (
		ctx: TranslationContext,
		key: TranslationKeyRef,
		params: Record<string, unknown>,
	): string => {
		const target = getKey(ctx, key.id)
		if (key.params === false) {
			params = {}
		} else if (Array.isArray(key.params)) {
			const newParams: Record<string, unknown> = {}
			for (const param of key.params) {
				if (!(param in params))
					throw new TypeError(`Missing param: ${param}`)
				newParams[safeProp(param)] = params[safeProp(param)]
			}
			params = newParams
		} else if (typeof params === 'object') {
			const newParams: Record<string, unknown> = {}
			for (const [k, v] of Object.entries(key.params)) {
				if (!(v in params)) throw new TypeError(`Missing param: ${v}`)
				newParams[safeProp(k)] = params[safeProp(v)]
			}
			params = newParams
		}
		return translateImpl(ctx, target, params)
	},
	enum: (
		ctx: TranslationContext,
		key: TranslationKeyEnum,
		params: Record<string, unknown>,
	): string => {
		const param = params[safeProp(key.key)]
		if (typeof param !== 'number' && typeof param !== 'string')
			throw new TypeError(
				`Param ${key.key} is not number or string (${typeof param})`,
			)
		idAssert<number | string>(param)
		// eslint-disable-next-line security/detect-object-injection
		const branch = key.choices[param] ?? key.default
		return translateImpl(ctx, branch, params)
	},
}

function translateImpl(
	ctx: TranslationContext,
	key: TranslationKey,
	params: Record<string, unknown>,
): string {
	if (typeof key === 'string') return key
	else return translateImpls[key.$t](ctx, magicCast(key), params)
}

function getKey(ctx: TranslationContext, key: string): TranslationKey {
	const value = ctx.keys[safeProp(key)]
	if (typeof value !== 'string' && typeof value !== 'object')
		throw new TypeError(`Key ${key} not defined`)
	return value
}

export const translate: Translate = (ctx, key, params = {}) => {
	try {
		return translateImpl(ctx, getKey(ctx, key), params)
	} catch (e) {
		console.warn('[translator] Translation error', { key, params }, e)
		return `{tr(${key}, ${JSON.stringify(params)})}`
	}
}

function msAdd<K, V>(ms: Map<K, Set<V>>, k: K, v: V) {
	let s = ms.get(k)
	if (!s) {
		s = new Set()
		ms.set(k, s)
	}
	s.add(v)
}

function translationInfoImpl(
	ctx: TranslationContext,
	value: TranslationKey,
	keys: Set<string>,
	params: Map<string, Set<TranslationValueKind>>,
): void {
	if (typeof value === 'string') return
	switch (value.$t) {
		case 'interpolate':
			msAdd(params, value.key, 'interpolate')
			break

		case 'concat':
			for (const key of value.parts)
				translationInfoImpl(ctx, key, keys, params)
			break

		case 'date':
			msAdd(params, value.key, 'date')
			break
		case 'time':
			msAdd(params, value.key, 'time')
			break
		case 'datetime':
			msAdd(params, value.key, 'datetime')
			break

		case 'list':
			msAdd(params, value.key, 'list')
			break

		case 'plural':
			msAdd(params, value.key, 'plural')
			for (const key of Object.values(value.choices)) {
				if (key) translationInfoImpl(ctx, key, keys, params)
			}
			break

		case 'ref': {
			keys.add(value.id)
			let newParams: Map<string, Set<TranslationValueKind>> = params
			if (value.params !== true) newParams = new Map()
			const next = getKey(ctx, value.id)
			translationInfoImpl(ctx, next, keys, newParams)
			if (Array.isArray(value.params)) {
				for (const param of value.params) {
					for (const kind of newParams.get(param) ?? [])
						msAdd(params, param, kind)
				}
			} else if (typeof value === 'object') {
				for (const [k, v] of Object.entries(value.params)) {
					for (const kind of newParams.get(k) ?? [])
						msAdd(params, v, kind)
				}
			}
			break
		}

		case 'enum':
			msAdd(params, value.key, 'enum')
			for (const key of Object.values(value.choices)) {
				if (key) translationInfoImpl(ctx, key, keys, params)
			}
			translationInfoImpl(ctx, value.default, keys, params)
			break
	}
}

export const translationInfo: TranslationInfo = (ctx, key) => {
	try {
		const keys = new Set<string>()
		const params = new Map<string, Set<TranslationValueKind>>()
		keys.add(key)
		translationInfoImpl(ctx, getKey(ctx, key), keys, params)
		return {
			keys: [...keys],
			params: Object.fromEntries(
				[...params.entries()].map(([k, v]) => [k, [...v]]),
			),
		}
	} catch {
		return null
	}
}
